import React, { type ComponentPropsWithRef, type FC, forwardRef, type PropsWithChildren } from 'react';

import shouldForwardProp from '@styled-system/should-forward-prop';
import styled from '@emotion/styled';
import { mqMin, type FlexProps, flex, compose } from '@lucid/theme';
import { type Lucid } from 'types/types';

export type ContainerTagsType = 'div' | 'section' | 'header' | 'main';

export interface ContainerProps extends FlexProps, ComponentPropsWithRef<'div'> {
  /** Sets the HTML tag for the Container component. */
  as?: ContainerTagsType;
  /** Allows the component to get additional styles. */
  className?: string;
  /** Adds additional padding to the Container at maxWidth breakpoint. */
  extraPadding?: number;
  /** Sets display: 'flex' on the Container. */
  displayFlex?: boolean;
  /** Sets the HTML id for the Container. */
  id?: string;
  /** Allows to update side padding for specific breakpoints without overwriting the remaining media queries */
  sidePadding?: Partial<Record<Lucid.ThemeUI.Breakpoints, string | number>>;
}

const containerStyleProps = compose(flex);

const StyledContainer: FC<PropsWithChildren<ContainerProps>> = styled('div', {
  shouldForwardProp,
})<ContainerProps>(({ extraPadding = 0, displayFlex, sidePadding }) => {
  const { default: defaultPx, ...px } = {
    default: '1.875rem',
    mobile: '',
    tabletSmall: '',
    tablet: '3.75rem',
    tabletLandscape: '1.75rem',
    computer: '3.75rem',
    maxWidth: `${7.5 + extraPadding}rem`,
    ...sidePadding,
  };

  const sidePaddingMqs = Object.entries(px).reduce(
    (prev, [breakpoint, value]) => ({
      ...prev,
      [mqMin[breakpoint as Lucid.ThemeUI.Breakpoints]]: {
        paddingRight: value,
        paddingLeft: value,
      },
    }),
    { paddingRight: defaultPx, paddingLeft: defaultPx },
  );

  return {
    label: 'container',
    display: displayFlex ? 'flex' : 'block',
    position: 'relative',
    maxWidth: '90rem',
    width: '100%',
    margin: '0 auto',
    height: '100%',
    WebkitFontSmoothing: 'antialiased',
    justifyContent: 'space-between',
    ...sidePaddingMqs,
  };
}, containerStyleProps);

const Container = forwardRef<HTMLDivElement, ContainerProps>(({ className, ...rest }, forwardedRef) => (
  <StyledContainer ref={forwardedRef} {...rest} className={className} />
));

export { Container };
export default Container;
